import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { X, Users, Play, Pause, Send, Radio, ArrowLeft, Copy, Loader2, UserPlus, Trash2 } from "lucide-react";
import UserPickerSheet from "../components/UserPickerSheet";

const REACTIONS = ["❤️", "🔥", "😂", "😮", "😍", "👏"];

/**
 * WatchParty — realtime shared reel viewing. Host controls playback; guests
 * follow the host's position/playing state via 2-second polling. Live chat +
 * emoji reactions render inside the reel column.
 */
export default function WatchParty() {
  const { id } = useParams();
  const nav = useNavigate();
  const videoRef = useRef(null);
  const lastMsgAt = useRef(null);
  const wsRef = useRef(null);
  const [party, setParty] = useState(null);
  const [me, setMe] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chat, setChat] = useState("");
  const [copied, setCopied] = useState(false);
  const [floats, setFloats] = useState([]); // transient reaction floaties
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteSending, setInviteSending] = useState(false);

  // Load party + me
  useEffect(() => {
    (async () => {
      try {
        const [p, u] = await Promise.all([
          api.get(`/watch-parties/${id}`),
          api.get(`/auth/me`).catch(() => ({ data: null })),
        ]);
        setParty(p.data); setMe(u.data);
        if (u.data && p.data?.host_id !== u.data.id) {
          await api.post(`/watch-parties/${id}/join`).catch(() => {});
        }
      } catch {
        nav("/");
      }
    })();
    return () => {
      api.post(`/watch-parties/${id}/leave`).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const isHost = me && party && me.id === party.host_id;

  // Realtime subscribe via WebSocket. Falls back to REST polling if WS fails
  // (keeps 2s polling as a safety net).
  useEffect(() => {
    if (!party) return;
    // 1. Try WebSocket
    try {
      const token = (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
      const url = `${base}/api/ws/watch/${id}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const evt = JSON.parse(ev.data);
          if (evt.type === "state") setParty(p => p ? { ...p, state: evt.state } : p);
          else if (evt.type === "message") {
            setMessages(prev => {
              if (prev.some(m => m.id === evt.message.id)) return prev;
              return [...prev, evt.message].slice(-100);
            });
            if (evt.message.kind === "reaction" && evt.message.reaction) {
              const fid = evt.message.id;
              setFloats(f => [...f, { id: fid, emoji: evt.message.reaction }]);
              setTimeout(() => setFloats(f => f.filter(x => x.id !== fid)), 3200);
            }
          }
        } catch { /* ignore */ }
      };
      // Keep-alive ping so the server sees activity every 30s
      const ping = setInterval(() => {
        try { ws.readyState === 1 && ws.send(JSON.stringify({ ping: 1 })); } catch { /* ignore */ }
      }, 30000);
      ws.onclose = () => clearInterval(ping);
    } catch { /* fallback path via polling below */ }

    // 2. Safety-net REST polling every 2s — captures viewer count updates the
    // WS doesn't fan out, and covers browsers/networks where WS is blocked.
    const tick = async () => {
      try {
        const [pr, mr] = await Promise.all([
          api.get(`/watch-parties/${id}`),
          api.get(`/watch-parties/${id}/messages${lastMsgAt.current ? `?since=${encodeURIComponent(lastMsgAt.current)}` : ""}`),
        ]);
        setParty(pr.data);
        const newMsgs = mr.data || [];
        if (newMsgs.length) {
          lastMsgAt.current = newMsgs[newMsgs.length - 1].created_at;
          setMessages(prev => {
            const seen = new Set(prev.map(m => m.id));
            const merged = [...prev, ...newMsgs.filter(m => !seen.has(m.id))];
            return merged.slice(-100);
          });
        }
      } catch { /* transient */ }
    };
    tick();
    const t = setInterval(tick, 3000);
    return () => {
      clearInterval(t);
      try { wsRef.current?.close(); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, party?.status]);

  // Non-host: enforce host's playback state on our video element
  useEffect(() => {
    if (!party?.state || !videoRef.current || isHost) return;
    const v = videoRef.current;
    const drift = Math.abs((v.currentTime || 0) - (party.state.position || 0));
    if (drift > 1.5) v.currentTime = party.state.position || 0;
    if (party.state.playing && v.paused) v.play().catch(() => {});
    if (!party.state.playing && !v.paused) v.pause();
  }, [party?.state, isHost]);

  // Host: push our playback state every 3s
  useEffect(() => {
    if (!isHost || !videoRef.current) return;
    const push = () => {
      const v = videoRef.current;
      api.post(`/watch-parties/${id}/state`, {
        position: v.currentTime || 0,
        playing: !v.paused,
      }).catch(() => {});
    };
    push();
    const t = setInterval(push, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHost, id]);

  const sendChat = async () => {
    const t = chat.trim(); if (!t) return;
    setChat("");
    try {
      const r = await api.post(`/watch-parties/${id}/message`, { text: t });
      setMessages(m => [...m, r.data]);
    } catch { /* ignore */ }
  };

  const react = async (emoji) => {
    try {
      const r = await api.post(`/watch-parties/${id}/message`, { reaction: emoji });
      setMessages(m => [...m, r.data]);
      setFloats(f => [...f, { id: r.data.id, emoji }]);
      setTimeout(() => setFloats(f => f.filter(x => x.id !== r.data.id)), 3200);
    } catch { /* ignore */ }
  };

  const copyInvite = () => {
    const url = `${window.location.origin}/watch/${id}`;
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {});
  };

  const sendInvites = async (chosen) => {
    setInviteOpen(false);
    if (!chosen || chosen.length === 0) return;
    setInviteSending(true);
    try {
      await api.post(`/watch-parties/${id}/invite`, {
        user_ids: chosen.map(u => u.id),
        message: `Watching "${party?.reel?.title || 'a reel'}" — join us!`,
      });
      // Post a local system-style message so the host sees confirmation
      setMessages(m => [...m, {
        id: `local-invite-${Date.now()}`,
        kind: "system",
        text: `📩 Invited ${chosen.length} friend${chosen.length > 1 ? "s" : ""}`,
        created_at: new Date().toISOString(),
      }]);
    } catch { /* ignore */ }
    finally { setInviteSending(false); }
  };

  const deleteReel = async () => {
    if (!party?.reel?.id) return;
    if (!window.confirm("Delete this reel? The watch party will end for everyone. This cannot be undone.")) return;
    try {
      await api.delete(`/reels/${party.reel.id}`);
      nav("/");
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not delete reel");
    }
  };

  if (!party) {
    return <div className="flex items-center justify-center h-[80vh]" data-testid="watch-loading"><Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]"/></div>;
  }

  const ended = party.status === "ended";
  const viewers = (party.viewer_ids || []).length;

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-black" data-testid="watch-party-page">
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b border-white/10 bg-gradient-to-b from-black/90 to-transparent">
        <button onClick={() => nav("/")} data-testid="wp-back" className="p-2 rounded-full bg-white/8"><ArrowLeft className="w-4 h-4"/></button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FF2A5F] text-white font-extrabold flex items-center gap-1"><Radio className="w-2.5 h-2.5 animate-pulse"/>WATCH PARTY</span>
            <span className="text-xs text-white/60 flex items-center gap-1"><Users className="w-3 h-3"/>{viewers}</span>
          </div>
          <div className="font-outfit font-extrabold text-sm truncate mt-0.5">{party.title}</div>
        </div>
        <button onClick={() => setInviteOpen(true)} data-testid="wp-invite-friends"
          className="text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#A855F7] text-black font-extrabold flex items-center gap-1 active:scale-95">
          <UserPlus className="w-3 h-3"/>Invite
        </button>
        <button onClick={copyInvite} data-testid="wp-invite"
          className="text-[10px] px-2 py-1.5 rounded-full bg-white/10 text-white/85 font-bold flex items-center gap-1 active:scale-95">
          <Copy className="w-3 h-3"/>{copied ? "Copied" : "Link"}
        </button>
        {isHost && me?.id && party?.reel && (party.reel.user_id === me.id || party.reel.user?.id === me.id) && (
          <button onClick={deleteReel} data-testid="wp-delete-reel"
            className="text-[10px] px-2 py-1.5 rounded-full bg-[#FF2A5F]/15 border border-[#FF2A5F]/40 text-[#FF2A5F] font-bold flex items-center gap-1 active:scale-95"
            aria-label="Delete reel">
            <Trash2 className="w-3 h-3"/>Delete
          </button>
        )}
      </div>

      {ended && (
        <div className="p-3 text-center text-sm text-[#FF2A5F] font-bold bg-[#FF2A5F]/10 border-b border-[#FF2A5F]/30">
          This watch party has ended. <Link to="/" className="underline">Back to reels →</Link>
        </div>
      )}

      {/* Player + chat rail */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        <div className="relative flex-1 bg-black flex items-center justify-center overflow-hidden">
          {party.reel?.video_url ? (
            <video
              ref={videoRef}
              src={party.reel.video_url}
              className="max-h-full max-w-full object-contain"
              autoPlay={party.state?.playing}
              controls={isHost}
              playsInline
              loop
              data-testid="wp-video"
            />
          ) : <div className="text-white/50 text-sm">Reel unavailable</div>}

          {/* Reaction floaties */}
          <div className="absolute right-4 bottom-4 pointer-events-none">
            {floats.map(f => (
              <div key={f.id} className="text-3xl absolute right-0 animate-[wp-float_3s_ease-out_forwards]" style={{ bottom: 0 }}>{f.emoji}</div>
            ))}
          </div>

          {/* Reaction bar */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-1.5 pointer-events-auto">
            {REACTIONS.map(e => (
              <button key={e} onClick={() => react(e)} data-testid={`wp-react-${e}`}
                className="w-9 h-9 rounded-full bg-black/45 backdrop-blur border border-white/15 flex items-center justify-center text-lg active:scale-90 hover:bg-white/15">{e}</button>
            ))}
          </div>
        </div>

        {/* Chat rail */}
        <div className="md:w-80 md:flex-shrink-0 md:border-l md:border-white/10 flex flex-col bg-[#0B0F19]" style={{ maxHeight: "40vh" }}>
          <div className="p-2 border-b border-white/10 flex items-center gap-1.5">
            <img src={party.host?.avatar} alt="" className="w-6 h-6 rounded-full border border-[#00E5FF]"/>
            <div className="text-[11px] font-bold flex-1 truncate">@{party.host?.username} (host)</div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5" data-testid="wp-messages">
            {messages.filter(m => m.kind !== "reaction").map(m => (
              <div key={m.id} className={`text-[11px] ${m.kind === "system" ? "text-white/50 italic" : ""}`}>
                {m.kind === "system" ? (
                  m.text
                ) : (
                  <div className="flex items-start gap-1.5">
                    <img src={m.user?.avatar} alt="" className="w-5 h-5 rounded-full flex-shrink-0"/>
                    <div className="flex-1 min-w-0">
                      <span className="font-bold text-[#00E5FF] mr-1">@{m.user?.username}</span>
                      <span className="text-white/90 break-words">{m.text}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="p-2 border-t border-white/10 flex items-center gap-1.5">
            <input value={chat} onChange={e => setChat(e.target.value)}
              onKeyDown={e => e.key === "Enter" && sendChat()}
              placeholder="Say something…" data-testid="wp-chat-input"
              className="flex-1 px-3 py-2 rounded-full bg-white/5 border border-white/10 text-xs outline-none"/>
            <button onClick={sendChat} data-testid="wp-chat-send"
              disabled={!chat.trim()}
              className="w-9 h-9 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#A855F7] flex items-center justify-center active:scale-90 disabled:opacity-40">
              <Send className="w-4 h-4 text-black"/>
            </button>
          </div>
        </div>
      </div>

      <UserPickerSheet
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onDone={sendInvites}
        kind="mention"
        title="Invite friends to Watch Party"
        multi/>

      {inviteSending && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/85 border border-white/15 text-sm font-bold shadow-lg z-[90]">
          Sending invites…
        </div>
      )}
    </div>
  );
}

// Inline keyframe for the reaction floaties (scoped via style tag once)
if (typeof document !== "undefined" && !document.getElementById("wp-floaty-style")) {
  const s = document.createElement("style"); s.id = "wp-floaty-style";
  s.textContent = "@keyframes wp-float { 0% { transform: translateY(0) scale(1); opacity: 1; } 100% { transform: translateY(-140px) scale(1.4); opacity: 0; } }";
  document.head.appendChild(s);
}
